import { TabContext, TabPanel } from '@mui/lab';
import { Chip, Stack, Tab, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import React from 'react';
import { CutterHistory, MaintenanceSchedule } from '../../types/cutterHistory';
import HelpIcon from '../../help/HelpIcon';
import TabsWithExport from '../../common/TabsWithExport';
import CutterDetailsContractualDataTest from './CutterDetailsHistory.erpcontract';
import CutterDetailsHistoryServices from './CutterDetailsHistory.acts';
import CutterDetailsHistoryPlan from './CutterDetailsHistory.plan';
import LogToDB from '../../types/dbLogs';
import { getFormatedStringFromDate, getDurationStringFromDays, getZipFileLink } from '../../utils/dateUtils';

export interface CutterDetailsHistoryMaintenanceProps {
    cutter: CutterHistory;
    isVersalis: boolean;
    horizontalTabValue: string;
    onHorizontalTabChange: (event: React.SyntheticEvent, newValue: string) => void;
    commonDataGridProps: any;
    horizontalTabsProps: any;
    tabPanelSx: any;
    selectedRowId?: number | null;
}

export const SnapshotChip: React.FC<{ snapshotDate: Date | null; daysSinceSnapshot: number | null; sourceZip?: string }> = 
({ snapshotDate, daysSinceSnapshot, sourceZip }) => {
    const snapshotChipColor =
        daysSinceSnapshot == null ? 'default' :
        daysSinceSnapshot <= 2   ? 'success' :
        daysSinceSnapshot <= 14  ? 'info' :
        daysSinceSnapshot <= 30  ? 'warning' : 'error';

    // Resolved once at module load — stable for the session
    const tzAbbr = new Intl.DateTimeFormat(navigator.language, { timeZoneName: 'shortOffset' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value ?? '';

    return (
         (snapshotDate) && (
                    <Stack direction="row" gap={2} alignItems="center" sx={{ mb: 1, px: 1, pt: 1 }}>
                        {snapshotDate && daysSinceSnapshot != null && (
                            <Chip
                                color={snapshotChipColor as any}
                                size="small"
                                label={`Snapshot: ${getDurationStringFromDays(daysSinceSnapshot)}`}
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                            />
                        )}
                        {sourceZip && (
                            <>
                            <Typography variant="caption" >Source:&nbsp;</Typography>
                            <Typography
                                component="a"
                                href={getZipFileLink(sourceZip, true)}
                                download={`${sourceZip}.zip`}
                                variant="caption"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                   color: 'text.primary',  textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {sourceZip} ⬇
                            </Typography>
                            </>
                        )}
                    </Stack>
                )
    )
}



export const CutterDetailsHistoryMaintenance: React.FC<CutterDetailsHistoryMaintenanceProps> = ({
    cutter,
    commonDataGridProps,
    horizontalTabValue,
    onHorizontalTabChange,
    horizontalTabsProps,
    selectedRowId,
}) => {

    const interventions = (cutter.maintenanceInterventions ?? []).slice(0, 30);
    const schedule = (cutter.maintenanceSchedule ?? []).slice(0, 30);

    const latestRow = schedule.reduce<MaintenanceSchedule | null>(
        (max, s) => max === null || new Date(s.scheduleDate) > new Date(max.scheduleDate ?? 0) ? s : max, null
    );
    const snapshotDate = latestRow ? new Date(latestRow.scheduleDate) : null;
    const sourceZip = latestRow?.zipFileName;
    const daysSinceSnapshot = snapshotDate ? (Date.now() - snapshotDate.getTime()) / 86_400_000 : null;


    // Log when user opens acts or plan tab for the first time
    React.useEffect(() => {
        if (horizontalTabValue === 'acts' || horizontalTabValue === 'plan') {
            void LogToDB(horizontalTabValue, cutter.serialNumber);
        }
    }, [horizontalTabValue, cutter.serialNumber]);

    const dataToExcelCols = (d: any[]): Array<{ key: string; header: string }> =>
        d.length === 0 ? [] : Object.keys(d[0]).map(k => ({ key: k, header: k }));

    const activeExport = React.useMemo(() => {
        switch (horizontalTabValue) {
            case 'acts':
                return interventions.length > 0
                    ? { data: interventions, columns: dataToExcelCols(interventions), label: `${cutter.serialNumber} maintenance-acts` }
                    : null;
            case 'plan':
                return schedule.length > 0
                    ? { data: schedule, columns: dataToExcelCols(schedule), label: `${cutter.serialNumber} maintenance-plan` }
                    : null;
            default:
                return null;
        }
    }, [horizontalTabValue, interventions, schedule]);

    return (


        
        <TabContext value={horizontalTabValue}>
            <TabsWithExport
                value={horizontalTabValue}
                onChange={onHorizontalTabChange}
                {...horizontalTabsProps}
                sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525'
                }}
                aria-label="Maintenance tabs"
                exportData={activeExport}>
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Contract</Typography>
                        <HelpIcon helpContext="details-maintenance-contract" placement="right" size="small" />
                    </Stack>
                } value='contract' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Acts ({interventions.length > 0 ? interventions.length : 'No data'})</Typography>
                        <HelpIcon helpContext="details-maintenance-acts" placement="right" size="small" />
                    </Stack>
                } value='acts' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Plan ({schedule.length > 0 ? schedule.length : 'No data'})</Typography>
                        <HelpIcon helpContext="details-maintenance-plan" placement="right" size="small" />
                    </Stack>
                } value='plan' />
            </TabsWithExport>
            <TabPanel value='contract' sx={{ p: 0 }}>
                <CutterDetailsContractualDataTest serialNumber={cutter.serialNumber} />
            </TabPanel>
            <TabPanel value='acts' sx={{ p: 0 }}>
                <>
                {(snapshotDate) && <SnapshotChip snapshotDate={snapshotDate}  daysSinceSnapshot={daysSinceSnapshot} sourceZip={sourceZip} />}
                <CutterDetailsHistoryServices
                    interventions={interventions}
                    selectedRowId={horizontalTabValue === 'acts' ? selectedRowId : null}
                    commonDataGridProps={commonDataGridProps}
                />
                </>
            </TabPanel>
            <TabPanel value='plan' sx={{ p: 0 }}>
               <>
                {(snapshotDate) && <SnapshotChip snapshotDate={snapshotDate}  daysSinceSnapshot={daysSinceSnapshot} sourceZip={sourceZip} />}
                <CutterDetailsHistoryPlan commonDataGridProps={commonDataGridProps}
                    schedule={schedule}
                />
                </>
            </TabPanel>
        </TabContext>
    );
};

export default CutterDetailsHistoryMaintenance;
